<template>
  <div class="page-section tu-scope">
    <div :class="containerClass">
      <h2 class="mb-2">Programs</h2>
      <p class="mb-4">Compare tuition, pass rates and application dates across schools.</p>

      <div class="tu-toolbar" role="search" aria-label="Filter programs">
        <tu-filter-search
          :value="filters.search"
          placeholder="Search programs, schools, or cities"
          label="Search programs, schools, or cities"
          @input="filters.search = $event"
        />
        <tu-filter-select
          :value="filters.program"
          label="Program"
          placeholder="All programs"
          :options="programOptions"
          aria-label="Filter by program"
          @change="filters.program = $event"
        />
        <tu-filter-select
          :value="filters.state"
          label="State"
          placeholder="All states"
          :options="stateOptions"
          aria-label="Filter by state"
          @change="filters.state = $event; filters.city = ''"
        />
        <tu-filter-select
          :value="filters.city"
          label="City"
          placeholder="All cities"
          :options="cityOptions"
          aria-label="Filter by city"
          @change="filters.city = $event"
        />
        <tu-filter-select
          :value="filters.type"
          label="School type"
          placeholder="All school types"
          :options="typeOptions"
          aria-label="Filter by school type"
          @change="filters.type = $event"
        />
      </div>

      <div class="tu-results">
        <p class="tu-count" role="status" aria-live="polite">
          <template v-if="isLoading && !rows.length">Loading programs…</template>
          <template v-else>
            {{ filtered.length.toLocaleString('en-US') }} program{{ filtered.length === 1 ? '' : 's'
            }}{{ hasActiveFilters ? ' match your filters' : ' to explore' }}
          </template>
        </p>
        <div class="tu-results-tools">
          <button v-if="hasActiveFilters" type="button" class="tu-clear" @click="clearFilters">
            Clear filters <span aria-hidden="true">×</span>
          </button>
          <tu-filter-toggle
            label="Upcoming deadlines only"
            compact
            :active="filters.deadline"
            @toggle="filters.deadline = !filters.deadline"
          />
          <tu-filter-select
            :value="filters.sort"
            label="Sort by"
            compact
            :clearable="false"
            :options="sortOptions"
            aria-label="Sort programs"
            @change="filters.sort = $event"
          />
        </div>
      </div>

      <div v-if="isLoading && !rows.length" class="tu-card-grid" aria-busy="true">
        <div v-for="i in 6" :key="`s-${i}`" class="tu-skel"></div>
      </div>

      <div v-else-if="!filtered.length" class="tu-empty">
        <md-icon>menu_book</md-icon>
        <h4 class="tu-empty-title">No programs match just yet</h4>
        <p class="tu-empty-text">Try a different location or clear your filters to see more programs.</p>
        <button v-if="hasActiveFilters" type="button" class="tu-btn tu-btn--ghost tu-empty-cta" @click="clearFilters">
          Clear filters
        </button>
      </div>

      <div v-else class="tu-card-grid">
        <uni-program-card
          v-for="row in visible"
          :key="row.key"
          :offering="row.offering"
          :school="row.school"
          :to="routeFor(row)"
          :media="false"
        />
      </div>

      <div v-if="visible.length < filtered.length" class="tu-more">
        <button type="button" class="tu-btn tu-btn--ghost" @click="limit += 48">Show more programs</button>
        <p class="tu-count">Showing {{ visible.length }} of {{ filtered.length }} matching programs</p>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import TuFilterSearch from '@/components/Ui/TuFilterSearch.vue';
import TuFilterSelect from '@/components/Ui/TuFilterSelect.vue';
import TuFilterToggle from '@/components/Ui/TuFilterToggle.vue';
import { filterProgramEntries, loadProgramDirectory } from '@/common/tuProgram';
import { TU_DIRECTORY_PROGRAMS } from '@/common/tuDirectoryPrograms';
import UniProgramCard from '../Dashboard/UniProgramCard.vue';

/**
 * Student portal Programs page. Mirrors transitionu.health/programs: every
 * degree program at every school, with search, program / state / city / type
 * filters in the bar, and deadlines + sort in the results row.
 */
export default {
  name: 'ProgramsList',
  components: { TuFilterSearch, TuFilterSelect, TuFilterToggle, UniProgramCard },
  extends: Page,

  data() {
    return {
      title: 'Programs',
      isLoading: true,
      programs: TU_DIRECTORY_PROGRAMS,
      rows: [],
      limit: 48,
      now: Date.now(),
      filters: { search: '', program: '', state: '', city: '', type: '', deadline: false, sort: 'name' },
    };
  },

  computed: {
    programOptions() {
      return this.programs.map(p => ({ value: p.id, text: p.title }));
    },
    stateOptions() {
      return this.options('state');
    },
    cityOptions() {
      return this.options('city', this.rows.filter(r => !this.filters.state || r.school.state === this.filters.state));
    },
    typeOptions() {
      return this.options('institution_type').map(o => ({ value: o.value, text: this.typeLabel(o.value) }));
    },
    sortOptions() {
      return [
        { value: 'name', text: 'School name: A\u2013Z' },
        { value: 'state', text: 'State: A\u2013Z' },
        { value: 'tuition', text: 'Lowest tuition' },
        { value: 'pass', text: 'Highest pass rate' },
        { value: 'deadline', text: 'Upcoming deadline' },
      ];
    },
    filtered() {
      return filterProgramEntries(this.rows.slice(), this.filters, this.now);
    },
    visible() {
      return this.filtered.slice(0, this.limit);
    },
    hasActiveFilters() {
      const f = this.filters;
      return !!(f.search || f.program || f.state || f.city || f.type || f.deadline);
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.limit = 48;
      },
    },
  },

  methods: {
    ...mapActions('uni', ['getAllUniversitiesByState']),
    get,

    options(key, rows = this.rows) {
      return [...new Set(rows.map(r => r.school[key]).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map(v => ({ value: v, text: v }));
    },
    typeLabel(type) {
      return String(type)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    routeFor(row) {
      return { name: 'student-program-detail', params: { uniSlug: row.school.slug, programSlug: row.program.slug } };
    },
    clearFilters() {
      this.filters = { search: '', program: '', state: '', city: '', type: '', deadline: false, sort: this.filters.sort };
    },

    async fetchPage(params) {
      const response = await this.getAllUniversitiesByState(params);
      return get(response, 'data', response);
    },

    async fetchDirectory() {
      this.isLoading = true;
      try {
        const { entries } = await loadProgramDirectory(this.fetchPage, this.programs);
        this.rows = entries;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    await this.fetchDirectory();
  },
};
</script>

<style lang="scss" scoped>
.tu-more {
  display: grid;
  justify-items: center;
  gap: 12px;
  margin-top: 40px;
}
</style>
